/* eslint-disable max-lines */
// deps
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { API_SUCCESS_STATUS_CODE } from "@swiggy-private/core-utils";
import {
    CheckoutActionTypes,
    CheckoutContext,
    closeWebview,
    DineoutCartRendererActionPayload,
    DineoutCheckoutRoute,
    useNavigate,
    DineoutCartContext,
    DineoutCartType,
    IDineoutCartResponseBillData,
    IDineoutLocalCart as ILocalCart,
    IResponseCartItem,
    ISuperSavingBottomWidgetSubscriptionDataProps,
    BLAZE_EVENTS,
    NEWRELIC_TABLES,
    recordNREvent,
    getBridgeSdk,
} from "@swiggy-private/checkout-core";
import {
    CoreLayout,
    isNetworkError,
    RouteProps,
    useEffectOnce,
    isCurrentSystemThemeDark,
} from "@swiggy-private/core-ui";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";

// interfaces
import { IBillEntries, ICartPageErrorPayload } from "@dineout-cart-client/interfaces";

// hooks
import useFetchDineoutCart from "@dineout-cart-client/hooks/useFetchDineoutCart";

import { useDineoutBookingAnalytics } from "@dineout-cart-client/hooks/useDineoutCartAnalytics";

// constants
import {
    CUSTOM_ERROR_PAYLOAD,
    CUSTOM_HANDLING_ERRORCODES,
    EMPTY_STATE_HEADER,
    EVENT_HEADER_TEXT,
    GENERIC_HANDLING_ERRORCODES,
    EMPTY_TICKET_STATE_TEXT,
    REMOVE_SOLD_OUT_ITEMS,
    PROCEED,
    RECORD_PERCIEVED_TIME_EVENTS,
    EMPTY_TABLE_RESERVATION_CART_TEXTS,
    GO_BACK,
    EVENTS_REFERER,
    STATUS_BAR_COLOR_DEFAULT,
    STATUS_BAR_COLOR_DARK,
    SUBSCRIPTION_UPSELL_CALLOUT_KEY,
    PAYMENT_OPTION_IDS,
    ERROR_IMAGE_MAX_DIMENSIONS,
} from "@dineout-cart-client/constants";

// utils
import {
    getBillDetails,
    getBillEntriesList,
    getBookingTicketMeta,
    getDealCategory,
    getReservationTicketMeta,
    getRestaurantId,
    getSavingWidgetData,
    getTicketDetailsIdList,
    getSuperSavingBottomWidget,
    isCartDataNonEmpty,
} from "@dineout-cart-client/utils/index";
import { recordCartPercievedTime } from "@dineout-cart-client/utils/Metric";

// components
import Header from "./components/header/Header";

import Content from "./components/content/Content";
import { UpdateProgressLoader } from "@swiggy-private/core-ui";

// styles
import { ToastContainer } from "./styles";

// contexts
import {
    getEventId,
    getEventTicketDetails,
    getHeaderData,
    isAnyItemUnavailable,
    getPartialPaymentWidget,
} from "@dineout-cart-client/utils/EventBooking";

// utils
import { getApplyCouponWidget } from "@dineout-cart-client/utils/Coupon";
import { CartMetaData } from "@dineout-cart-client/interfaces/dineoutCartTypes";
import { getCtaSubTextFromCallouts, isEventReservationCart } from "@dineout-cart-client/helpers/cartHelper";
import { logDealCartEvent } from "@dineout-cart-client/helpers/Clevertap";
import { isTurboEnabled } from "@dineout-cart-client/helpers/TurboPayment";
import { UpdateCartContext } from "@dineout-cart-client/contexts";

const TableReservationCartWrapper = (_props: RouteProps<DineoutCheckoutRoute>) => {
    const [softError, setSoftError] = useState<string>();
    const [initialCartLoaded, setInitialCartLoaded] = useState(false);
    const { cartState: cartPayload, cartType } = useContext(DineoutCartContext);
    const { localCart, reviewedCart } = cartPayload;
    const cartState = reviewedCart?.data;
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const isEventsBookingFlow =
        (checkoutState.payload as DineoutCartRendererActionPayload)?.isEventBookingFlow ||
        isEventReservationCart(cartType); // use cartType if already present, if not decide based on checkoutState
    const _cartType = isEventsBookingFlow ? DineoutCartType.EVENT_BOOKING_CART : DineoutCartType.TABLE_RESERVATION_CART; // to set cart type on initial load
    const {
        loading,
        selectedOfferId,
        selectedSlot,
        error: cartApiError,
        uniqueIdForCart,
    } = useFetchDineoutCart(_cartType);

    const { doUpdateCart, updateCartLoading, updateCartError } = useContext(UpdateCartContext);
    const restaurantId = getRestaurantId(cartState);
    const dealCategory = getDealCategory(cartState);
    const _isTurboEnabled = isTurboEnabled();
    const eventId = getEventId(cartState);
    const navigateTo = useNavigate();

    // UI elements specific to Table Reservation Cart
    const reservationTicketData = getReservationTicketMeta(cartState?.items?.[0]);
    const superSavingBottomWidget = getSuperSavingBottomWidget(cartState);

    // UI elements specific to Event Booking Flow
    const rewardsWidgetData = cartState?.rewardsWidget;
    const itemRewardsWidgetData = cartState?.items?.[0]?.rewardsWidget;

    const eventsBookingTicketMeta = getBookingTicketMeta(cartState?.items?.[0], cartState?.storesInfo?.[0]);
    const eventsTicketSectionHeaderMeta = useMemo(
        () => getHeaderData(cartState?.storesInfo?.[0], cartState?.aggregatedItemsInfo),
        [cartState],
    );
    const eventTicketDetails = useMemo(
        () => getEventTicketDetails(cartState?.items, localCart?.items, cartState?.aggregatedItemsInfo),
        [cartState, localCart],
    );
    const isCustomFreeEventBookingFlow = useMemo(() => {
        return localCart?.cartMetaData?.referrer === EVENTS_REFERER;
    }, [localCart?.cartMetaData?.referrer]);

    const ticketDetails = useMemo(
        () => getTicketDetailsIdList(eventTicketDetails?.itemList || []),
        [eventTicketDetails],
    );

    // UI elements common to Event Booking Flow and Table Reservation Cart
    const savingWidgetData = useMemo(() => {
        return getSavingWidgetData(cartState?.widgets);
    }, [cartState]);
    const billEntries: IBillEntries[] = useMemo(
        () => getBillDetails(cartState, isEventsBookingFlow) as IBillEntries[],
        [cartState, isEventsBookingFlow],
    );

    const applyCouponWidget = getApplyCouponWidget(cartState);
    const partialPaymentWidget = getPartialPaymentWidget(cartState);

    // analytics events
    const {
        onClickViewOneBenefits,
        onImpressionBillDetails,
        onImpressionOnePurchase,
        onImpressionViewTableBookingCart,
        onClickConfirmBookingTableReviewPage,
        onImpressionTicketDetails,
        onClickEventProceed,
        onImpressionEventProceed,
        onImpressionProceedCtaForTableBooking,
        onScreenViewBookingReview,
        onClickTicketItem,
        onClickDeleteTicketItem,
        onClickTermsAndConditions,
        onClickTor,
        onImpressionDisclaimerBanner,
        onImpressionBenefitCallout,
        onClickTermsAndConditionsForEventBooking,
        onImpressionCashBackBenefitCallout,
        onImpressionTurboPayButtonReservation,
        onClickTurboPayButtonReservation,
        onClickChangePaymentMethod,
    } = useDineoutBookingAnalytics();

    useEffectOnce(() => {
        const _isCurrentSystemThemeDark = isCurrentSystemThemeDark();
        getBridgeSdk()
            .changeStatusBarColor({
                style: _isCurrentSystemThemeDark ? STATUS_BAR_COLOR_DARK : STATUS_BAR_COLOR_DEFAULT,
                statusBarMode: _isCurrentSystemThemeDark ? "lightContent" : "default",
                bottomBarMode: _isCurrentSystemThemeDark ? "lightContent" : "default",
            })
            .catch(() => void 0);
    });

    useEffect(() => {
        if (isEventsBookingFlow || isCustomFreeEventBookingFlow) {
            onScreenViewBookingReview({ restaurantId, eventId, ticketId: ticketDetails });
        }
    }, [
        eventId,
        isCustomFreeEventBookingFlow,
        isEventsBookingFlow,
        onScreenViewBookingReview,
        restaurantId,
        ticketDetails,
    ]);

    useEffect(() => {
        const cartItem = cartState?.items?.[0] || ({} as IResponseCartItem);
        const billDetailsData = cartState?.bill || ({} as IDineoutCartResponseBillData);
        const selectedDealCategory = billDetailsData?.dealCategory;
        const ids = {
            restaurantId: restaurantId || "",
            selectedOfferId: selectedOfferId || "",
            selectedSlotId: selectedSlot?.id || 0,
        };
        const subscriptionData =
            cartState?.superSavingBottomWidget?.subscriptionData ||
            ({} as ISuperSavingBottomWidgetSubscriptionDataProps);

        if (isEventsBookingFlow || isCustomFreeEventBookingFlow) return;

        if (isCartDataNonEmpty(ids, cartItem, selectedDealCategory, billEntries)) {
            onImpressionViewTableBookingCart(ids, cartItem, selectedDealCategory, billEntries);
        }
        if (!isEmpty(eventsBookingTicketMeta?.offerDetails?.title)) {
            onImpressionBenefitCallout({ offerText: eventsBookingTicketMeta?.offerDetails?.title as string });
        }
        if (!isEmpty(subscriptionData)) {
            onImpressionOnePurchase(subscriptionData);
        }
    }, [
        billEntries,
        cartState,
        eventsBookingTicketMeta?.offerDetails?.title,
        isCustomFreeEventBookingFlow,
        isEventsBookingFlow,
        onImpressionBenefitCallout,
        onImpressionOnePurchase,
        onImpressionViewTableBookingCart,
        restaurantId,
        selectedOfferId,
        selectedSlot?.id,
    ]);

    const billDetailsStr = useMemo(() => getBillEntriesList(billEntries), [billEntries]);

    const ctaSubTextFromCallouts = useMemo(
        () => getCtaSubTextFromCallouts(cartState?.callouts, SUBSCRIPTION_UPSELL_CALLOUT_KEY),
        [cartState?.callouts],
    );

    const eventsProceedButtonEvent = useCallback(
        (isImpression = false) => {
            const isItemUnavailable = isAnyItemUnavailable(eventTicketDetails);

            const guests = eventTicketDetails?.itemList?.reduce((acc: number, item) => acc + (item.quantity || 0), 0);
            const buttonStatus = isItemUnavailable ? REMOVE_SOLD_OUT_ITEMS : PROCEED;
            const eventProceedProp = {
                restaurantId: restaurantId || "",
                eventId: eventId || "",
                ticketDetails: ticketDetails || {},
                dealCategory: dealCategory || "",
                guests: guests || 0,
                billDetails: billDetailsStr || {},
                buttonStatus,
            };
            if (isImpression) {
                onImpressionEventProceed(eventProceedProp);
            } else {
                onClickEventProceed(eventProceedProp);
            }
        },
        [
            eventTicketDetails,
            restaurantId,
            eventId,
            ticketDetails,
            dealCategory,
            billDetailsStr,
            onImpressionEventProceed,
            onClickEventProceed,
        ],
    );

    useEffect(() => {
        if (isEventsBookingFlow || isCustomFreeEventBookingFlow) {
            const tickets = cartState?.items?.map((item: IResponseCartItem) => {
                return {
                    ticketId: String(item.itemVariantId),
                    numberOfGuests: item.guestCount,
                    amount: item.price,
                };
            });

            if (!isEmpty(restaurantId) && !isEmpty(eventId) && !isEmpty(dealCategory) && !isEmpty(billDetailsStr)) {
                onImpressionTicketDetails({
                    restaurantId: restaurantId || "",
                    eventId: eventId || "",
                    tickets,
                });
                eventsProceedButtonEvent(true);
            }
        } else {
            if (!isEmpty(restaurantId) && !isEmpty(eventId) && !isEmpty(dealCategory) && !isEmpty(billDetailsStr)) {
                onImpressionProceedCtaForTableBooking({
                    restaurantId: restaurantId || "",
                    guests: eventsBookingTicketMeta?.bookingDetails?.noOfGuests || 0,
                    selectedOfferId: eventId || "",
                    dealCategory: dealCategory || "",
                    billDetails: billDetailsStr || {},
                    selectedDate: eventsBookingTicketMeta?.bookingDetails?.date || "",
                    selectedSlot: eventsBookingTicketMeta?.bookingDetails?.time || "",
                    selectedOfferText: eventsBookingTicketMeta?.offerDetails?.title || "",
                    selectedSlotId: selectedSlot?.id || 0,
                });
            }
        }
    }, [
        cartState,
        eventId,
        eventsProceedButtonEvent,
        eventTicketDetails,
        onImpressionTicketDetails,
        restaurantId,
        isEventsBookingFlow,
        onImpressionProceedCtaForTableBooking,
        eventsBookingTicketMeta?.bookingDetails?.noOfGuests,
        eventsBookingTicketMeta?.bookingDetails?.date,
        eventsBookingTicketMeta?.bookingDetails?.time,
        eventsBookingTicketMeta?.offerDetails?.title,
        dealCategory,
        billDetailsStr,
        selectedSlot?.id,
        isCustomFreeEventBookingFlow,
    ]);

    useEffect(() => {
        if (!loading && cartState != null && !initialCartLoaded) {
            recordCartPercievedTime(
                BLAZE_EVENTS.RECORD_TABLE_BOOKING_CART_LOAD_TIME,
                RECORD_PERCIEVED_TIME_EVENTS.TABLE_BOOKING_APP_LOAD_TIME,
            );

            // Log Clevertap event for deal cart
            const cartItem = cartState?.items?.[0];
            if (!isEmpty(cartItem)) {
                logDealCartEvent({
                    payableAmount: cartState?.bill?.dineoutPricingInfo?.billToPay?.toString() ?? "0",
                    rxID: restaurantId ?? "",
                    dealDetail: {
                        reservationTime: eventsBookingTicketMeta?.bookingDetails?.time,
                        groupName: eventsBookingTicketMeta?.bookingDetails?.slotGroupTitle,
                        dealTitle: eventsBookingTicketMeta?.offerDetails?.title,
                        dealId: cartItem.itemId,
                        count: Number(cartItem.guestCount),
                        variantId: String(cartItem.itemVariantId),
                    },
                    rxLocality: cartState?.storesInfo?.[0]?.locality ?? "",
                    city: cartState?.storesInfo?.[0]?.cityName,
                    eventId: cartItem.itemId,
                    ticketId: String(cartItem.itemVariantId),
                });
            }

            setInitialCartLoaded(true);
            recordNREvent({
                eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CART_P0_COMPONENT_IMPRESSION,
                componentName: "initial_cart_successful_load",
            });
        }
    }, [cartState, initialCartLoaded, loading, restaurantId, eventsBookingTicketMeta, isEventsBookingFlow]);

    useEffect(() => {
        if (updateCartError && !isEmpty(updateCartError?.errorPayload?.error?.statusMessage)) {
            setSoftError(updateCartError?.errorPayload?.error?.statusMessage);
        }
    }, [updateCartError]);

    const isEmptyEventsCart = useMemo(() => {
        const { itemList = [] } = eventTicketDetails || {};
        const isEmptyCart = isEmpty(itemList);
        return isEventsBookingFlow && isEmptyCart;
    }, [eventTicketDetails, isEventsBookingFlow]);

    const clickEventOnConfirmBookingTable = useCallback(() => {
        const cartItem = cartState?.items?.[0] || ({} as IResponseCartItem);
        const billDetailsData = cartState?.bill || ({} as IDineoutCartResponseBillData);
        const selectedDealCategory = billDetailsData?.dealCategory;
        const ids = {
            restaurantId: restaurantId || "",
            selectedOfferId: selectedOfferId || "",
            selectedSlotId: selectedSlot?.id || 0,
        };

        if (isEventsBookingFlow || isCustomFreeEventBookingFlow) {
            eventsProceedButtonEvent();
        } else {
            onClickConfirmBookingTableReviewPage(ids, cartItem, selectedDealCategory, billEntries);
        }
    }, [
        billEntries,
        cartState?.bill,
        cartState?.items,
        eventsProceedButtonEvent,
        isCustomFreeEventBookingFlow,
        isEventsBookingFlow,
        onClickConfirmBookingTableReviewPage,
        restaurantId,
        selectedOfferId,
        selectedSlot?.id,
    ]);

    const errorPayload = cartApiError?.errorPayload;
    const errorStatusCode = errorPayload?.statusCode;
    const errorStatusMessage = errorPayload?.statusMessage;

    const showNetworkConnectivityScreen = useMemo(() => {
        return (
            // this is for when cart loads and we make the 1st api call
            !loading && cartApiError?.errorPayload && isNetworkError(cartApiError?.errorPayload)
        );
    }, [cartApiError, loading]);

    const onClickApplyCoupon = useCallback(() => {
        setCheckoutState({
            type: CheckoutActionTypes.SHOW_COUPON_LISTING,
            payload: {
                couponListingLaunchTimestamp: Date.now(),
            },
        });
        navigateTo(DineoutCheckoutRoute.COUPON_LISTING);
    }, [navigateTo, setCheckoutState]);

    const onClickRemoveCoupon = useCallback(() => {
        const updatedLocalCart: ILocalCart = {
            ...localCart,
            cartMetaData: {
                ...localCart?.cartMetaData,
                couponCode: null,
            } as CartMetaData,
        };
        doUpdateCart(updatedLocalCart);
    }, [localCart, doUpdateCart]);

    const onRedeemCash = useCallback(
        (redeemValue: boolean, redeemableRewards: number) => {
            const updatedLocalCart: ILocalCart = {
                ...localCart,
                cartMetaData: {
                    ...localCart?.cartMetaData,
                    rewardsWidget: {
                        rewardsRedeemed: redeemValue,
                        redeemableRewards: redeemableRewards,
                    },
                } as CartMetaData,
            };
            doUpdateCart(updatedLocalCart);
        },
        [localCart, doUpdateCart],
    );

    const handlePaymentOptionChange = useCallback(
        (selectedId: string) => {
            if (!localCart) return;

            const isPartialPaymentApplied = selectedId === PAYMENT_OPTION_IDS.PARTIAL_PAYMENT;

            const updatedLocalCart: ILocalCart = {
                ...localCart,
                cartMetaData: {
                    ...localCart.cartMetaData,
                    isPartialPaymentApplied,
                } as CartMetaData,
            };

            doUpdateCart(updatedLocalCart);
        },
        [localCart, doUpdateCart],
    );

    const resetSoftError = useCallback(() => {
        setSoftError(undefined);
    }, []);

    // Error handling
    const handleCustomError = CUSTOM_HANDLING_ERRORCODES.includes(errorStatusCode || API_SUCCESS_STATUS_CODE);
    const handleGenericError =
        !cartState ||
        showNetworkConnectivityScreen ||
        GENERIC_HANDLING_ERRORCODES.includes(errorStatusCode || API_SUCCESS_STATUS_CODE);

    let errorHandlingProps: ICartPageErrorPayload = {};

    if (handleCustomError) {
        errorHandlingProps = {
            title: CUSTOM_ERROR_PAYLOAD.TITLE,
            message: errorStatusMessage,
            imageId: CUSTOM_ERROR_PAYLOAD.IMAGE,
            action: {
                onClick: closeWebview,
                text: GO_BACK,
            },
            errorImageMaxWidth: ERROR_IMAGE_MAX_DIMENSIONS.WIDTH,
            errorImageMaxHeight: ERROR_IMAGE_MAX_DIMENSIONS.HEIGHT,
        };
    } else if (isEmptyEventsCart) {
        errorHandlingProps = {
            title: EMPTY_TICKET_STATE_TEXT.MAIN_TEXT,
            message: EMPTY_TICKET_STATE_TEXT.SUB_TEXT,
            imageId: isCurrentSystemThemeDark()
                ? EMPTY_TICKET_STATE_TEXT.IMAGE_URL.dark
                : EMPTY_TICKET_STATE_TEXT.IMAGE_URL.light,
            secondMessage: EMPTY_TICKET_STATE_TEXT.SUB_TEXT_SECOND,
            action: {
                onClick: closeWebview,
                text: EMPTY_TICKET_STATE_TEXT.BUTTON_TEXT,
            },
            errorImageMaxWidth: ERROR_IMAGE_MAX_DIMENSIONS.WIDTH,
            errorImageMaxHeight: ERROR_IMAGE_MAX_DIMENSIONS.HEIGHT,
        };
    } else if (isEmpty(cartState?.items)) {
        errorHandlingProps = {
            title: EMPTY_TABLE_RESERVATION_CART_TEXTS.TITLE,
            message: EMPTY_TABLE_RESERVATION_CART_TEXTS.MESSAGE,
            imageId: EMPTY_TABLE_RESERVATION_CART_TEXTS.IMAGE,
            action: {
                onClick: closeWebview,
                text: GO_BACK,
            },
            errorImageMaxWidth: ERROR_IMAGE_MAX_DIMENSIONS.WIDTH,
            errorImageMaxHeight: ERROR_IMAGE_MAX_DIMENSIONS.HEIGHT,
        };
    }

    const pageErrorMeta = {
        showError: handleCustomError || handleGenericError || isEmptyEventsCart,
        errorPayload: errorHandlingProps,
    };
    return (
        <CoreLayout>
            <>
                <CoreLayout.Header isDineoutCartHeader>
                    {!isEmpty(eventsBookingTicketMeta?.restaurantDetails) ? (
                        <Header
                            restaurantDetails={
                                isEventsBookingFlow ? EVENT_HEADER_TEXT : eventsBookingTicketMeta?.restaurantDetails
                            }
                            isEventsBookingFlow={isEventsBookingFlow}
                        />
                    ) : (
                        <Header
                            restaurantDetails={isEventsBookingFlow ? EVENT_HEADER_TEXT : EMPTY_STATE_HEADER}
                            isEventsBookingFlow={isEventsBookingFlow}
                        />
                    )}
                </CoreLayout.Header>
                <CoreLayout.Content isDineoutCart={true} hideScrollbar={true}>
                    {updateCartLoading && <UpdateProgressLoader />}
                    <Content
                        isInitialCartLoading={loading}
                        isEventsBookingFlow={isEventsBookingFlow}
                        isCustomFreeEventBookingFlow={isCustomFreeEventBookingFlow}
                        cartType={_cartType}
                        cartTypeFromLocalCart={localCart?.cartType}
                        savingWidgetData={savingWidgetData}
                        headerData={eventsTicketSectionHeaderMeta}
                        eventTicketDetails={eventTicketDetails}
                        bookingTicketMeta={eventsBookingTicketMeta}
                        billEntries={billEntries}
                        superSavingBottomWidget={superSavingBottomWidget}
                        skipPayment={cartState?.bill?.skipPayment}
                        billAmount={cartState?.bill?.dineoutPricingInfo?.billToPay}
                        ticketData={reservationTicketData}
                        dealCategory={dealCategory}
                        ids={{ restaurantId: restaurantId || "", eventId: eventId || "" }}
                        selectedOfferId={selectedOfferId}
                        clickEventOnConfirmBookingTable={clickEventOnConfirmBookingTable}
                        onImpressionBillDetails={onImpressionBillDetails}
                        onClickViewOneBenefits={onClickViewOneBenefits}
                        selectedSlot={selectedSlot}
                        onClickTicketItem={onClickTicketItem}
                        onClickDeleteTicketItem={onClickDeleteTicketItem}
                        eventsProceedButtonEvent={eventsProceedButtonEvent}
                        showToastError={setSoftError}
                        applyCouponWidget={applyCouponWidget}
                        onClickApplyCoupon={onClickApplyCoupon}
                        onClickRemoveCoupon={onClickRemoveCoupon}
                        pageErrorMeta={pageErrorMeta}
                        onClickTermsAndConditions={onClickTermsAndConditions}
                        onClickTor={onClickTor}
                        onImpressionDisclaimerBanner={onImpressionDisclaimerBanner}
                        onClickTermsAndConditionsForEventBooking={onClickTermsAndConditionsForEventBooking}
                        rewardsWidgetData={rewardsWidgetData}
                        itemRewardsWidgetData={itemRewardsWidgetData}
                        cashbackPerc={cartState?.bill?.dineoutPricingInfo?.totalDiscountPercentage || 0}
                        onRedeemCash={onRedeemCash}
                        onImpressionCashBackBenefitCallout={onImpressionCashBackBenefitCallout}
                        ctaSubTextFromCallouts={ctaSubTextFromCallouts}
                        _isTurboEnabled={_isTurboEnabled}
                        onImpressionTurboPayButtonReservation={onImpressionTurboPayButtonReservation}
                        onClickTurboPayButtonReservation={onClickTurboPayButtonReservation}
                        onClickChangePaymentMethod={onClickChangePaymentMethod}
                        partialPaymentWidget={partialPaymentWidget}
                        onPaymentOptionChange={handlePaymentOptionChange}
                        uniqueIdForCart={uniqueIdForCart}
                    />
                    {!isEmpty(softError) && (
                        <ToastContainer>
                            <Toast
                                message={softError}
                                containerClass={"toast"}
                                show
                                autoDismiss
                                onDismiss={resetSoftError}
                            />
                        </ToastContainer>
                    )}
                </CoreLayout.Content>
            </>
        </CoreLayout>
    );
};

export default memo(TableReservationCartWrapper);
